import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bot, RefreshCw, MailCheck } from 'lucide-react';

const brl = (v) => (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
const quando = (v) => {
  if (!v) return 'nunca executou';
  const d = new Date(v);
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  const rel = min < 1 ? 'agora mesmo' : min < 60 ? `há ${min} min` : `há ${Math.floor(min / 60)} h`;
  return `${d.toLocaleString('pt-BR')} · ${rel}`;
};

const Metrica = ({ rotulo, valor, cor }) => (
  <div>
    <div className={`text-2xl font-bold ${cor || ''}`}>{valor}</div>
    <p className="text-xs text-muted-foreground">{rotulo}</p>
  </div>
);

export const PainelFilaAutomatica = () => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get('/fiscal/cron/painel');
      setDados(data);
    } catch { /* painel é informativo */ }
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const exec = dados?.ultima_execucao;
  const res = exec?.resultado || {};
  const alerta = exec?.alerta_email;

  return (
    <Card data-testid="painel-fila-automatica">
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bot className="w-5 h-5 text-blue-600" />
            Fila automática de NFS-e
          </CardTitle>
          <CardDescription>
            Roda {dados?.cron?.cadencia || 'a cada 15 minutos'} · última execução:{' '}
            <span data-testid="painel-ultima-execucao">{quando(exec?.created_at)}</span>
            {exec?.status && <Badge variant="outline" className="ml-2">{exec.status}</Badge>}
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={carregar} disabled={carregando}
          data-testid="painel-fila-atualizar">
          <RefreshCw className={`w-4 h-4 ${carregando ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <Metrica rotulo="Autorizadas" valor={res.autorizadas ?? 0} cor="text-emerald-600" />
          <Metrica rotulo="Rejeitadas" valor={res.rejeitadas ?? 0} cor="text-red-600" />
          <Metrica rotulo="Aguardando prefeitura" valor={res.aguardando ?? 0} cor="text-amber-600" />
          <Metrica rotulo="Encerradas / removidas" valor={res.removidas_da_fila ?? 0} />
          <Metrica rotulo="Na fila agora" valor={dados?.fila_total ?? 0} />
        </div>

        {alerta && (
          <p className="flex items-center gap-2 text-xs text-slate-600" data-testid="painel-alerta-email">
            <MailCheck className="w-4 h-4 text-emerald-600" />
            {alerta.enviado
              ? `Aviso de rejeição enviado para ${alerta.destino}`
              : `Falha ao enviar o aviso de rejeição: ${alerta.erro || 'verifique o e-mail configurado'}`}
          </p>
        )}

        {dados?.pendentes?.length > 0 && (
          <div data-testid="painel-pendentes">
            <p className="mb-2 text-xs uppercase tracking-wide text-slate-500">
              Pendentes na fila ({dados.pendentes.length})
            </p>
            <div className="space-y-1">
              {dados.pendentes.slice(0, 6).map((p) => (
                <div key={p.nota_id} className="flex flex-wrap items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm">
                  <span className="font-medium">{p.aluno_nome || p.tomador_nome || 'Sem nome'}</span>
                  <span className="text-slate-500">R$ {brl(p.valor_servicos)}</span>
                  {p.competencia_mes && (
                    <span className="text-slate-500">
                      {String(p.competencia_mes).padStart(2, '0')}/{p.competencia_ano}
                    </span>
                  )}
                  <Badge variant="outline">{p.status || p.fila_status}</Badge>
                  {p.erro_mensagem && (
                    <span className="text-xs text-red-600">{p.erro_mensagem}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {dados?.rejeitadas_recentes?.length > 0 && (
          <div data-testid="painel-rejeitadas">
            <p className="mb-2 text-xs uppercase tracking-wide text-slate-500">
              Rejeições recentes da prefeitura
            </p>
            <div className="space-y-1">
              {dados.rejeitadas_recentes.slice(0, 5).map((n) => (
                <div key={n.id} className="rounded-md border border-red-100 bg-red-50 px-3 py-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{n.aluno_nome || n.tomador_nome || 'Sem nome'}</span>
                    <span className="text-slate-500">R$ {brl(n.valor_servicos)}</span>
                    <span className="text-slate-500">RPS {n.rps_numero || '-'}</span>
                  </div>
                  <p className="text-xs text-red-700">{n.erro_mensagem || 'sem detalhe da prefeitura'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {!exec && (
          <p className="text-sm text-muted-foreground">
            A fila automática ainda não rodou neste ambiente.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
